import type { ConfirmPopupProps } from '@neo/blocks/confirm-popup'
import type { MenuItem } from '@neo/blocks/context-menu'
import type { DataTableRow } from '@neo/blocks/cx-data-table/types'
import type { RUserSession } from 'v2/types/api/auth'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { IntegrationState } from '../utils/constants'
import type { ExternalAppRow } from './external-app-actions'

interface ExternalAppsContextMenuHandlers {
  setPopupState: (state: ConfirmPopupProps) => void
  hasPermission: (module: RbacModules, permission: RbacPermissionTypes) => boolean
  handleToggleState: (app: ExternalAppRow) => void
  handleReconnect: (app: ExternalAppRow) => void
  handleDelete: (app: ExternalAppRow) => void
  handleCustomFields: (app: ExternalAppRow) => void
}

export const getExternalAppsContextMenu = (
  row?: ExternalAppRow,
  _data?: DataTableRow<ExternalAppRow>[],
  _hoveredElement?: React.RefObject<HTMLElement | null>,
  handlers?: ExternalAppsContextMenuHandlers,
  currentUser?: RUserSession,
): MenuItem<ExternalAppRow>[] | undefined => {
  if (!row || row.isGroup || !handlers) return []

  const {
    setPopupState,
    hasPermission,
    handleToggleState,
    handleReconnect,
    handleDelete,
    handleCustomFields,
  } = handlers
  if (currentUser?.aom_uuid !== row.integrated_by?.id) return []

  const [RECONNECT, ENABLE, DISABLE, DELETE, CUSTOM_FIELDS] = [
    'Reconnect',
    'Enable',
    'Disable',
    'Delete',
    'Custom Fields',
  ]
  const actions: string[] = []
  // `state` is a bare string off the wire; the rest of this flow uppercases
  // before comparing, and a lowercase payload here would leave only Delete.
  const state = row.state?.toUpperCase()
  if (row.is_custom_fields_enabled) actions.push(CUSTOM_FIELDS)
  if (state === IntegrationState.DISABLED) actions.push(ENABLE)
  else if (state === IntegrationState.ACTIVE) actions.push(DISABLE)
  else if (state === IntegrationState.EXPIRED) actions.push(RECONNECT)
  actions.push(DELETE)

  const permittedActions = actions.filter((action) => {
    if (action !== DELETE && !hasPermission(RbacModules.integration, RbacPermissionTypes.edit)) {
      return false
    }
    return action !== DELETE || hasPermission(RbacModules.integration, RbacPermissionTypes.delete)
  })

  // No actions => no menu at all. Returning just the 'Actions' label produced a
  // menu whose entire contents were the word "Actions" on any row whose actions
  // RBAC filtered away.
  if (permittedActions.length === 0) return []

  return [
    { type: 'label' as const, label: 'Actions' },
    ...permittedActions.map((action) => ({
      type: 'item' as const,
      label: action,
      onSelect: () => {
        if (action === RECONNECT) return handleReconnect(row)
        if (action === CUSTOM_FIELDS) return handleCustomFields(row)
        if (action === ENABLE || action === DISABLE) {
          const enabled = action === ENABLE
          setPopupState({
            open: true,
            title: `${action} App`,
            description: `Are you sure you want to ${action.toLowerCase()} the app "${row.name}"?`,
            okButtonProps: {
              variant: enabled ? 'default' : 'destructive',
              onClick: () => handleToggleState(row),
            },
          })
          return
        }
        if (action === DELETE) {
          setPopupState({
            open: true,
            title: 'Delete App',
            description: `Are you sure you want to delete the app "${row.name}"?`,
            okButtonProps: {
              variant: 'destructive',
              onClick: () => handleDelete(row),
            },
          })
        }
      },
    })),
  ]
}
